import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, X, Download, Printer, Edit, Trash2, AlertTriangle } from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';

const ShipmentList = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [activeSearchKeyword, setActiveSearchKeyword] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // 'New' for unapproved shipments
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [shipmentToDelete, setShipmentToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const tableContainerRef = useRef(null);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Toast notification effect
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Load shipments
  const loadShipments = async (page = 1, keyword = '', type = '', append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError('');

      const skip = (page - 1) * pageSize;
      const requestData = {
        Skip: skip,
        Take: pageSize
      };

      if (keyword) {
        requestData.Keyword = keyword;
      }

      if (type) {
        requestData.Type = type;
      }

      const response = await MainService.GetShipmentList(requestData);

      if (response && response.Data) {
        if (append) {
          setShipments(prev => [...prev, ...response.Data]);
        } else {
          setShipments(response.Data);
        }
        setTotalCount(response.Total || 0);
        setCurrentPage(page);
        
        // Scroll to top after loading new page
        if (page > 1 && tableContainerRef.current) {
          tableContainerRef.current.scrollTop = 0;
        }
      } else {
        setError(ui('shipments.list.loadFailed'));
        if (!append) {
          setShipments([]);
          setTotalCount(0);
        }
      }
    } catch (err) {
      console.error('Error loading shipments:', err);
      setError(ui('shipments.list.errorGeneric'));
      if (!append) {
        setShipments([]);
        setTotalCount(0);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    loadShipments(1, activeSearchKeyword, typeFilter);
  }, [activeSearchKeyword, typeFilter]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (!tableContainerRef.current || isLoadingMore || loading) return;
      
      const container = tableContainerRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      
      // Check if scrolled to bottom (within 50px)
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        // Check if there are more pages to load
        if (currentPage < totalPages) {
          loadShipments(currentPage + 1, activeSearchKeyword, typeFilter, true);
        }
      }
    };

    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [currentPage, totalPages, activeSearchKeyword, typeFilter, isLoadingMore, loading]);

  const handleSearch = () => {
    setCurrentPage(1);
    setActiveSearchKeyword(searchKeyword);
  };

  const handleClearSearch = () => {
    setSearchKeyword('');
    setActiveSearchKeyword('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadShipments(newPage, activeSearchKeyword, typeFilter);
    }
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    setSearchKeyword('');
    setActiveSearchKeyword('');
    setTypeFilter('');
    loadShipments(1, '', '');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const handleExportExcel = () => {
    if (shipments.length === 0) {
      setError(ui('shipments.list.noDataExport'));
      return;
    }

    try {
      const worksheetData = shipments.map((shipment, index) => ({
        [ui('columns.sNo')]: index + 1,
        [ui('columns.refNo')]: shipment.RefNo || '-',
        [ui('columns.date')]: formatDate(shipment.Date),
        [ui('columns.deliveryNote')]: shipment.DeliveryNote?.RefNo || '-',
        [ui('columns.driver')]: shipment.DeliveryNote?.DriverName || '-',
        [ui('columns.expectedAmount')]: shipment.ExpectedAmount || 0,
        [ui('columns.totalExpense')]: shipment.TotalExpense || 0,
        [ui('columns.cashInHand')]: shipment.CashInHand || 0,
        [ui('columns.totalIncome')]: shipment.TotalIncome || 0,
        [ui('columns.totalVat')]: shipment.TotalVat || 0,
        [ui('columns.totalAdvance')]: shipment.TotalAdvance || 0,
        [ui('columns.balanceIncome')]: shipment.BalanceIncome || 0,
        [ui('columns.totalMoneyFromClient')]: shipment.TotalMoneyFromClient || 0,
        [ui('columns.totalClientMoney')]: shipment.TotalClientMoney || 0,
        [ui('columns.remarks')]: shipment.Remarks || '-',
        [ui('common.status')]: shipment.IsApproved
          ? ui('shipments.list.statusApproved')
          : ui('shipments.list.statusPending'),
        [ui('columns.createdBy')]: shipment.CreatedBy?.UserName || '-',
        [ui('columns.createdDate')]: formatDate(shipment.CreatedDate),
      }));

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, ui('shipments.list.sheetName'));
      XLSX.writeFile(workbook, `Shipments_${new Date().toISOString().split('T')[0]}.xlsx`);
      setSuccess(ui('shipments.list.exportSuccess'));
    } catch (err) {
      console.error('Error exporting Excel:', err);
      setError(ui('shipments.list.exportFailed'));
    }
  };

  const handlePrint = async (shipmentId) => {
    try {
      setLoading(true);
      const response = await MainService.PrintShipment(shipmentId);
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank');
      }
    } catch (err) {
      console.error('Error printing shipment:', err);
      setError(ui('shipments.list.printFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (shipmentId) => {
    if (!shipmentId) return;
    navigate(`/home/shipments/edit-shipment/${shipmentId}`);
  };

  const handleDeleteClick = (shipment) => {
    setShipmentToDelete(shipment);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShipmentToDelete(null);
    setShowDeleteConfirm(false);
  };

  const confirmDeleteShipment = async () => {
    if (!shipmentToDelete?.Id) return;

    try {
      setLoading(true);
      setError('');
      const response = await MainService.DeleteShipment(shipmentToDelete.Id);

      if (response === 'Success' || response === true || response === 'true') {
        setSuccess(ui('shipments.list.deleteSuccess'));
        cancelDelete();
        await loadShipments(1, activeSearchKeyword, typeFilter);
        return;
      }

      setError(typeof response === 'string' ? response : ui('shipments.list.deleteFailed'));
    } catch (err) {
      console.error('Error deleting shipment:', err);
      setError(ui('shipments.list.deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-20 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-40 min-w-[300px]">
          <X className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-20 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-40 min-w-[300px]">
          <X className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'shipmentList')}</h1>
            <p className="text-sm text-gray-500 mt-1">{ui('shipments.list.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={ui('shipments.list.searchPlaceholder')}
                  className="pl-9 pr-8 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm w-48"
                />
                {searchKeyword && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <button
                onClick={handleSearch}
                disabled={loading}
                className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={ui('common.search')}
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700">{ui('shipments.list.typeLabel')}</label>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              >
                <option value="">{ui('shipments.list.typeAll')}</option>
                <option value="New">{ui('shipments.list.typeNew')}</option>
              </select>
            </div>
            <button
              onClick={handleExportExcel}
              disabled={loading || shipments.length === 0}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={ui('common.exportExcel')}
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={ui('common.refresh')}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div 
          ref={tableContainerRef}
          className="overflow-x-auto max-h-[600px] overflow-y-auto"
        >
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.sNo')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.refNo')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.date')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.deliveryNote')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.driver')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.expectedAmount')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.totalExpense')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.cashInHand')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.totalIncome')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.totalVat')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('common.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.createdBy')}</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.options')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && shipments.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-6 py-12 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">{ui('shipments.list.loading')}</p>
                  </td>
                </tr>
              ) : shipments.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-6 py-12 text-center text-gray-500">
                    <p className="text-lg mb-2">{ui('shipments.list.noShipments')}</p>
                  </td>
                </tr>
              ) : (
                shipments.map((shipment, index) => (
                  <tr key={shipment.Id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {shipment.RefNo || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(shipment.Date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {shipment.DeliveryNote?.RefNo || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {shipment.DeliveryNote?.DriverName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      AED {formatCurrency(shipment.ExpectedAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      AED {formatCurrency(shipment.TotalExpense)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      AED {formatCurrency(shipment.CashInHand)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      AED {formatCurrency(shipment.TotalIncome)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      AED {formatCurrency(shipment.TotalVat)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        shipment.IsApproved 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {shipment.IsApproved ? ui('shipments.list.statusApproved') : ui('shipments.list.statusPending')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {shipment.CreatedBy?.UserName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(shipment.Id)}
                          className="p-1.5 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded transition-colors"
                          title={ui('common.edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePrint(shipment.Id)}
                          className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                          title={ui('common.print')}
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(shipment)}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title={ui('common.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Loading indicator for infinite scroll */}
        {isLoadingMore && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-center">
              <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">{ui('shipments.list.loadingMore')}</span>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {ui('shipments.list.showingLoadedOf', { loaded: shipments.length, total: totalCount })}
                {currentPage < totalPages && (
                  <span className="ml-2 text-gray-500">{ui('shipments.list.scrollToLoadMore')}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading || isLoadingMore}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {ui('common.previous')}
                </button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  {ui('orders.orderList.pageOf', { page: currentPage, totalPages })}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading || isLoadingMore}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {ui('common.next')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showDeleteConfirm && shipmentToDelete && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
              <div className="rounded-full bg-red-100 p-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {ui('shipments.list.confirmDeleteTitle')}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {ui('shipments.list.confirmDeleteMessage', { refNo: shipmentToDelete.RefNo || '-' })}
                </p>
              </div>
              <button
                type="button"
                onClick={cancelDelete}
                className="ml-auto rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                disabled={loading}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 p-5">
              <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                <p><span className="font-medium">{ui('columns.refNo')}:</span> {shipmentToDelete.RefNo || '-'}</p>
                <p><span className="font-medium">{ui('columns.deliveryNote')}:</span> {shipmentToDelete.DeliveryNote?.RefNo || '-'}</p>
                <p><span className="font-medium">{ui('common.status')}:</span> {shipmentToDelete.IsApproved ? ui('shipments.list.statusApproved') : ui('shipments.list.statusPending')}</p>
              </div>
              <p className="text-sm text-gray-600">
                {ui('shipments.list.confirmDeleteWarning')}
              </p>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={cancelDelete}
                disabled={loading}
                className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                {ui('common.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmDeleteShipment}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                {loading ? ui('shipments.list.deleting') : ui('common.delete')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ShipmentList;

